import React from 'react';
import { cn } from '../ui-x';

/**
 * The reference table shell (spec "Tables"): rounded-2xl card, surface thead,
 * divide-y body, cells px-4 py-3. A header item may set `numeric: true` to
 * right-align and tabular-align its column.
 */
/**
 * @param {{
 *   header?: Array<{ title?: any, key?: any, numeric?: boolean }>,
 *   column?: any[][],
 *   label?: any,
 *   onRowClick?: (rowIndex: number) => void,
 * }} props
 *
 * onRowClick (optional) makes whole rows clickable, e.g. to open a details
 * page. Clicks on a link or button inside the row are left to that element,
 * and keyboard users keep using the row's own link. Tables that don't pass
 * it are unchanged.
 */
const Table = ({ header, column, label, onRowClick }) => {
  const handleRowClick = (event, rowIndex) => {
    if (!onRowClick) return;
    if (event.target.closest('a, button, input, select, textarea, label')) return;
    onRowClick(rowIndex);
  };

  return (
    <div className="w-full overflow-hidden rounded-2xl border bg-card shadow-ui-sm">
      {label && (
        <div className="border-b px-4 py-4 sm:px-5">
          <h2 className="font-display text-base font-bold text-brand-navy">{label}</h2>
        </div>
      )}

      {/* min-w is what makes the overflow-x-auto above mean anything. A
          table that is only w-full can never be wider than its container,
          so instead of scrolling it squeezed its columns until the content
          was unreadable - visible as rows that only appeared when the
          browser was zoomed out. Below this width it scrolls sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] text-sm">
          <thead className="bg-surface text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              {header?.map((item) => (
                <th
                  scope="col"
                  className={cn('px-4 py-3 align-bottom', item.numeric && 'text-right')}
                  key={item.key}
                >
                  {item.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {column?.map((row, i) => (
              <tr
                className={cn('transition-colors hover:bg-surface/60', onRowClick && 'cursor-pointer')}
                onClick={(event) => handleRowClick(event, i)}
                key={i}
              >
                {row?.map((item, index) => (
                  <td
                    className={cn(
                      'px-4 py-3 align-top text-brand-navy',
                      header?.[index]?.numeric && 'text-right tabular-nums'
                    )}
                    key={index}
                  >
                    {item}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
